import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {ApplicationCommonService} from '../../services/application.common.service';
import {
  approveApplication,
  approveApplicationFailure,
  approveApplicationSuccess,
  createApplication,
  createApplicationFailure,
  createApplicationSuccess,
  fakeSignApplication,
  fakeSignApplicationSuccess,
  loadApplication,
  loadApplicationFailure,
  loadApplicationList,
  loadApplicationListFailure,
  loadApplicationListSuccess,
  loadApplicationSuccess,
  loadEntityTypes,
  loadEntityTypesFailure,
  loadEntityTypesSuccess,
  loadPayments,
  loadPaymentsFailure,
  loadPaymentsSuccess,
  loadPurposeFeeCollection,
  loadPurposeFeeCollectionFailure,
  loadPurposeFeeCollectionSuccess,
  loadRoadOccupationTypes,
  loadRoadOccupationTypesFailure,
  loadRoadOccupationTypesSuccess,
  loadServiceAttachmentPositions,
  loadServiceAttachmentPositionsFailure,
  loadServiceAttachmentPositionsSuccess,
  loadShipment,
  loadShipmentFailure,
  loadShipments,
  loadShipmentsFailure,
  loadShipmentsSuccess,
  loadShipmentSuccess,
  sendApplication,
  sendApplicationFailure,
  sendApplicationSuccess,
  signApplication,
  signApplicationFailure,
  signApplicationSuccess,
  updateAdPlacementOccupations,
  updateAdPlacementOccupationsFailure,
  updateAdPlacementOccupationsSuccess,
  updateApplicants,
  updateApplicantsSuccess,
  updateApplication,
  updateApplicationAttachments,
  updateApplicationAttachmentsFailure,
  updateApplicationAttachmentsSuccess,
  updateApplicationFailure,
  updateApplicationSuccess,
  updateContractors,
  updateContractorsFailure,
  updateContractorsSuccess,
  updateLocalisationData,
  updateLocalisationDataFailure,
  updateLocalisationDataSuccess,
  updateLocationOfDevicesUnrelatedToRoadOccupations,
  updateLocationOfDevicesUnrelatedToRoadOccupationsFailure,
  updateLocationOfDevicesUnrelatedToRoadOccupationsSuccess,
  updateOccupationOfLanePlacementConstructionObjects,
  updateOccupationOfLanePlacementConstructionObjectsFailure,
  updateOccupationOfLanePlacementConstructionObjectsSuccess,
  updateOccupations,
  updateOccupationsFailure,
  updateOccupationsSuccess,
  updateParcels,
  updateParcelsFailure,
  updateParcelsSuccess,
  updateVehicles,
  updateVehiclesFailure,
  updateVehiclesSuccess
} from '../actions/application.actions';
import {catchError, map, of, switchMap} from 'rxjs';
import {
  ApplicationService,
  EntityTypeAppEntityDictionaryEntityType,
  EntityTypeService,
  RoadOccupationPurposeFeeService,
  RoadOccupationTypeService,
  ShipmentService
} from '../../openapi';
import {ShipmentCommonService} from '../../services/shipment.common.service';
import {ParcelsCommonService} from '../../services/parcels.common.service';
import {RoadOccupationCommonService} from '../../services/road-occupation.common.service';
import {VehicleCommonService} from '../../services/vehicle.common.service';

@Injectable()
export class ApplicationEffects {
  constructor(
    private actions$: Actions,
    private applicationCommonService: ApplicationCommonService,
    private applicationService: ApplicationService,
    private roadOccupationTypeService: RoadOccupationTypeService,
    private shipmentCommonService: ShipmentCommonService,
    private shipmentService: ShipmentService,
    private parcelsCommonService: ParcelsCommonService,
    private entityTypeService: EntityTypeService,
    private roadOccupationCommonService: RoadOccupationCommonService,
    private vehicleCommonService: VehicleCommonService,
    private roadOccupationPurposeFeeService: RoadOccupationPurposeFeeService
  ) {
  }

  loadPurposeFeeCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadPurposeFeeCollection),
      switchMap(action =>
        this.roadOccupationPurposeFeeService.getRoadOccupationPurposeFeeCollectionRoadOccupationPurposeFeeCollection({
          purposeId: action.purposeId,
          pagination: false
        }).pipe(
          map(purposeFeeCollection => loadPurposeFeeCollectionSuccess({purposeFeeCollection})),
          catchError(() => of(loadPurposeFeeCollectionFailure()))
        )
      )
    )
  );

  updateLocalisationData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateLocalisationData),
      switchMap(action =>
        this.applicationCommonService.updateLocalisationData(
          action.currentApp,
          action.payload,
          action.parcels,
          action.locationDecisions
        ).pipe(
          map(application => {
            action.successFnc(application?.uuid || '');
            return updateLocalisationDataSuccess({application});
          }),
          catchError(() => of(updateLocalisationDataFailure()))
        )
      )
    )
  );

  loadServiceAttachmentPositions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadServiceAttachmentPositions),
      switchMap((action) =>
        this.applicationCommonService.loadAttachmentPositionsByApplicationId(action.appId)
          .pipe(
            map(serviceAttachmentPositions => loadServiceAttachmentPositionsSuccess({serviceAttachmentPositions})),
            catchError(() => of(loadServiceAttachmentPositionsFailure()))
          )
      )
    )
  );

  loadEntityTypes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadEntityTypes),
      switchMap(() =>
        this.entityTypeService.getEntityTypeCollectionEntityTypeCollection({pagination: false})
          .pipe(
            map(v => {
              const naturalPerson: EntityTypeAppEntityDictionaryEntityType = v.filter(type => type.code === 'OSOBA_FIZYCZNA')[0] as EntityTypeAppEntityDictionaryEntityType;
              const institution: EntityTypeAppEntityDictionaryEntityType = v.filter(type => type.code === 'OSOBA_FIZYCZNA_DG')[0] as EntityTypeAppEntityDictionaryEntityType;
              const companyForm: EntityTypeAppEntityDictionaryEntityType = v.filter(type => type.code === 'OSOBA_PRAWNA')[0] as EntityTypeAppEntityDictionaryEntityType;
              return [naturalPerson, institution, companyForm].filter(type => !!type);
            }),
            map(entityTypes => loadEntityTypesSuccess({entityTypes})),
            catchError(() => of(loadEntityTypesFailure()))
          )
      )
    )
  );

  createApplication$ = createEffect(() => this.actions$.pipe(
    ofType(createApplication),
    switchMap(action =>
      (action?.applicants?.length ? this.applicationCommonService.createApplication(action.serviceTypeId, action.applicants, action?.application) : this.applicationService.postApplicationApplicationCollection({
        application: {...action.application, service: action.serviceTypeId}
      })).pipe(
        map(application => {
          action.successFnc(application?.uuid || '');
          return createApplicationSuccess({application});
        }),
        catchError(() => {
          action.failureFnc();
          return of(createApplicationFailure());
        })
      )
    )
  ));

  loadApplication$ = createEffect(() => this.actions$.pipe(
    ofType(loadApplication),
    switchMap(action =>
      this.applicationService.getApplicationItemApplicationItem({
        id: action.id
      }).pipe(
        map(application => loadApplicationSuccess({application})),
        catchError(() => of(loadApplicationFailure()))
      )
    )
  ));

  updateApplicationData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateApplication),
      switchMap(action =>
        this.applicationService.patchApplicationApplicationItem({id: action.id, application: action.application})
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateApplicationSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateApplicationFailure());
            })
          )
      )
    )
  );

  updateLocationData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateParcels),
      switchMap((action) =>
        this.parcelsCommonService.updateParcels(action.currentApp, action.payload, action.parcels).pipe(
          map(application => {
            action.successFnc(application?.uuid || '');
            return updateParcelsSuccess({application});
          }),
          catchError(() => {
            action.failureFnc();
            return of(updateParcelsFailure());
          })
        )
      )
    )
  );

  updateVehicles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateVehicles),
      switchMap(action =>
        this.vehicleCommonService.updateVehicles(action.currentApp, action.payload, action.vehicles)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateVehiclesSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateVehiclesFailure());
            })
          )
      )
    )
  );

  updateApplicants$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateApplicants),
      switchMap(action =>
        this.applicationCommonService.updateApplicants(action.applicationId, action.applicants, action?.application)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateApplicantsSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateApplicationFailure());
            })
          )
      )
    )
  );

  updateContractors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateContractors),
      switchMap(action =>
        this.applicationCommonService.updateContractors(action.application, action.payload, action.contractors, action?.tag)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateContractorsSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateContractorsFailure());
            })
          )
      )
    )
  );

  updateOccupations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateOccupations),
      switchMap(action =>
        this.roadOccupationCommonService.updateOccupations(action.application, action.occupations)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateOccupationsSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateOccupationsFailure());
            })
          ))
    )
  );

  updateOccupationOfLanePlacementConstructionObjects$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateOccupationOfLanePlacementConstructionObjects),
      switchMap(action =>
        this.roadOccupationCommonService.occupationOfLanePlacementConstructionObjects(action.application, action.occupations)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateOccupationOfLanePlacementConstructionObjectsSuccess({application});
            }),
            catchError(() => of(updateOccupationOfLanePlacementConstructionObjectsFailure()))
          )
      )
    )
  );

  updateAdPlacementOccupation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateAdPlacementOccupations),
      switchMap(action =>
        this.roadOccupationCommonService.updateAdPlacementOccupations(action.application, action.occupations)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateAdPlacementOccupationsSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateAdPlacementOccupationsFailure());
            })
          )
      )
    )
  );

  updateLocationOfDevicesUnrelatedToRoadOccupations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateLocationOfDevicesUnrelatedToRoadOccupations),
      switchMap(action =>
        this.roadOccupationCommonService.updateLocationOfDevicesUnrelatedToRoadOccupations(action.application, action.occupations).pipe(
          map(application => {
            action.successFnc(application?.uuid || '');
            return updateLocationOfDevicesUnrelatedToRoadOccupationsSuccess({application});
          }),
          catchError(() => of(updateLocationOfDevicesUnrelatedToRoadOccupationsFailure()))
        )
      )
    )
  );

  updateApplicationAttachments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateApplicationAttachments),
      switchMap(action =>
        this.applicationCommonService.updateApplicationAttachments(action.application, action.attachments)
          .pipe(
            map(application => {
              action.successFnc(application?.uuid || '');
              return updateApplicationAttachmentsSuccess({application});
            }),
            catchError(() => {
              action.failureFnc();
              return of(updateApplicationAttachmentsFailure());
            })
          )
      )
    )
  );

  loadApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadApplicationList),
      switchMap(action =>
        this.applicationCommonService.loadApplicationList(action.code).pipe(
          map(applicationList => loadApplicationListSuccess({applicationList})),
          catchError(() => of(loadApplicationListFailure()))
        )
      )
    )
  );

  approveApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(approveApplication),
      switchMap(action =>
        this.applicationService.approveApplicationApplicationItem({
          id: action.applicationId,
          application: {}
        }).pipe(
          map(application => {
            action.successFnc();
            return approveApplicationSuccess({
              application
            });
          }),
          catchError(() => {
            action.failureFnc();
            return of(approveApplicationFailure());
          })
        )
      )
    )
  );

  loadRoadOccupationTypes$ = createEffect(() => this.actions$.pipe(
    ofType(loadRoadOccupationTypes),
    switchMap(() =>
      this.roadOccupationTypeService.getRoadOccupationTypeCollectionRoadOccupationTypeCollection({
        pagination: false
      }).pipe(
        map(roadOccupationTypes => loadRoadOccupationTypesSuccess({roadOccupationTypes})),
        catchError(() => of(loadRoadOccupationTypesFailure()))
      )
    )
  ));

  loadPayments$ = createEffect(() => this.actions$.pipe(
    ofType(loadPayments),
    switchMap(action =>
      this.applicationCommonService.loadApplicationPayments(action.applicationId)
        .pipe(
          map(applicationPayment => loadPaymentsSuccess({applicationPayment})),
          catchError(() => of(loadPaymentsFailure()))
        )
    )
  ));

  sendApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(sendApplication),
      switchMap(action => this.applicationService.sendApplicationApplicationItem({
          id: action.applicationId,
          application: {}
        }).pipe(
          map(() => {
            action.successFnc();
            return sendApplicationSuccess();
          }),
          catchError(() => {
            action.failureFnc();
            return of(sendApplicationFailure());
          })
        )
      )
    )
  );

  signApplication$ = createEffect(() => this.actions$.pipe(
    ofType(signApplication),
    switchMap(action =>
      this.applicationCommonService.signApplication(action.applicationId).pipe(
        map(result => {
          action.successFnc(result.redirect_url);
          return signApplicationSuccess();
        }),
        catchError(() => of(signApplicationFailure()))
      )
    )
  ));

  loadShipments$ = createEffect(() => this.actions$.pipe(
    ofType(loadShipments),
    switchMap(action =>
      this.shipmentCommonService.loadShipments(action.applicationId)
        .pipe(
          map(shipments => loadShipmentsSuccess({shipments})),
          catchError(() => of(loadShipmentsFailure()))
        )
    )
  ));

  loadShipment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadShipment),
      switchMap(action =>
        this.shipmentService.getShipmentItemShipmentItem({
          id: action.id
        }).pipe(
          map(shipment => loadShipmentSuccess({shipment})),
          catchError(() => of(loadShipmentFailure()))
        )
      )
    ));

  fakeSignApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fakeSignApplication),
      switchMap(action =>
        this.applicationService.fakeSignApplicationApplicationItem({id: action.applicationId, application: {}})
          .pipe(
            switchMap((application) => {
              action.successFnc();
              return [fakeSignApplicationSuccess(), loadApplication({id: action.applicationId})];
            }),
            catchError(() => {
              action.failureFnc();
              return of(signApplicationFailure());
            })
          )
      )
    )
  );

}
